import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ListBulletIcon, PlusIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { ItemList, propertyLabel } from '../components/items/ItemList';
import { ItemCards } from '../components/items/ItemCards';
import { CreateInspectionDialog } from '../components/inspections/CreateInspectionDialog';
import { EntityListPage } from '../components/templates/EntityListPage';
import { Cluster, HeaderActions, LoadMorePager, Select, SegmentedControl } from '../components/ui';
import { listInspections, inspectionsKeys, type InspectionListItem } from '../lib/api/inspections.gen';
import { useCursorList } from '../lib/query/useCursorList';
import { listSubjects, subjectsKeys, type Subject } from '../lib/api/subjects.gen';
import { listTemplates, templatesKeys } from '../lib/api/templates.gen';
import type { InspectionStatus } from '../types';
import { useAuth } from '../lib/auth';
import { useUrlState, urlStateEnum, urlStateString, urlStateInt, type UrlStateSchema } from '../hooks/useUrlState';
import { errorMessage, errorStatus } from '../lib/errorMessage';

type StatusFilter = InspectionStatus | 'all';

// Story 5-1a, widened server-side by 2-72c. This was `['all','draft',
// 'in_progress','completed']` — three of the nine states the lifecycle
// actually has (P05 §7). Every value the contract declares is PARSEABLE
// here, including the legacy `completed`, so a deep link minted before
// migration 012 still restores a filtered list rather than silently
// snapping back to "All" (G-25's contract is about the URL round-tripping,
// and a bookmark is exactly the caller that predates a migration). Story
// 2-72c: `status` now travels to the SERVER (`ListInspectionsQuery`,
// `contracts/inspections.js`) instead of filtering an already-fetched page —
// the pre-migration Joi schema this replaces only accepted three of these
// ten values, which is exactly why it used to stop at the client.
const STATUS_VALUES: readonly StatusFilter[] = [
  'all',
  'draft',
  'scheduled',
  'assigned',
  'in_progress',
  'submitted',
  'in_review',
  'approved',
  'delivered',
  'cancelled',
  'completed'
];

// What the page OFFERS is deliberately smaller than what it parses: `completed`
// is gone from the pills because migration 012 maps every such row to `approved`,
// so the filter would be a control that can only ever return an empty list.
// Offering it would be a lie about the data; parsing it is read-tolerance.
const filters: Array<{ label: string; value: StatusFilter }> = [
  { label: 'All', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Assigned', value: 'assigned' },
  { label: 'In progress', value: 'in_progress' },
  { label: 'Submitted', value: 'submitted' },
  { label: 'In review', value: 'in_review' },
  { label: 'Approved', value: 'approved' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Cancelled', value: 'cancelled' }
];

// Story 2-139, unchanged in shape by 2-72c: client-side sort, same reasoning
// as TemplateLibraryPage's identical `SortFilter`. `GET /v1/inspections` is
// the shared cursor-list contract (G-18) as of 2-72c, but its `ListQuery`
// (`contracts/list.js`) still only reserves a `sort` string for an endpoint
// to wire up — this one orders by `updatedAt desc` server-side and does not
// consume it (same unwired-reserved-field shape `TemplateLibraryPage`'s own
// comment records for `listTemplates`). `recent` (default, omitted from the
// URL) is therefore that server order; `property` re-sorts every
// currently-loaded row (every page fetched so far, not just the latest)
// client-side.
type SortFilter = 'recent' | 'property';
const SORT_VALUES: readonly SortFilter[] = ['recent', 'property'];
const SORT_LABEL: Record<SortFilter, string> = {
  recent: 'Recently updated',
  property: 'Property (A–Z)',
};

// Story 2-139 (P10 §2.4's collection form rule, docs/prd/10-design-system.md
// §2): List (`ItemList`, rows) is the default — an inspection row is
// genuinely operational (one identity, one status, one meta line, one
// action). Cards (`ItemCards`, composing the existing `TaskCard` primitive)
// is the alternate, touch-optimized view for a phone-first working queue —
// a real second rendering of the SAME data for a genuinely different task,
// not a dead affordance.
type ViewMode = 'list' | 'cards';
const VIEW_VALUES: readonly ViewMode[] = ['list', 'cards'];

// Real cursor page size (story 2-72c) — was a client-side slice width over
// an already-fetched, `.limit(200)`-capped array; now the `limit` sent to
// `useCursorList`/`listInspections`, same convention TemplateLibraryPage's
// own `PAGE_SIZE` follows.
const PAGE_SIZE = 20;

// G-25 URL-state schema (story 1G-13 landed `status`/`action`; story 2-139
// adds `sort`/`view`/`page` — the same one-hook, one-schema mechanism, not a
// second URL-state pattern). Module scope for referential stability.
const itemsListUrlSchema: UrlStateSchema<{
  status: StatusFilter;
  action: string;
  sort: SortFilter;
  view: ViewMode;
  // Live bug fix (2026-09): SiteDetailPage links to `/app/items?propertyId=
  // <subjectId>` to show "inspections at this site" (grep confirms it's the
  // only in-app producer of this param). The backend list endpoint
  // (`GET /v1/inspections`, `contracts/inspections.js → ListInspectionsQuery`)
  // accepts and applies `propertyId` server-side — this page just never read
  // it from the URL and forwarded it. Forwarded straight through to
  // `listInspections` and into the cursor-list query key, same as `status`
  // since 2-72c.
  propertyId: string;
  // How many CURSOR PAGES have been loaded beyond the first (story 2-72c;
  // 0 = just the first page) — same URL-owned pagination convention
  // TemplateLibraryPage's identical `page` state already uses. Restored on
  // reload/back by re-issuing `fetchNextPage()` that many times (the effect
  // below), unlike the pre-2-72c client-side slice this replaces, which
  // needed no restore effect because it never fetched.
  page: number;
}> = {
  status: urlStateEnum(STATUS_VALUES, 'all'),
  action: urlStateString(''),
  sort: urlStateEnum(SORT_VALUES, 'recent'),
  view: urlStateEnum(VIEW_VALUES, 'list'),
  propertyId: urlStateString(''),
  page: urlStateInt(0),
};

export function ItemsListPage(): JSX.Element {
  const [showDialog, setShowDialog] = useState(false);
  const [urlState, setUrlState] = useUrlState(itemsListUrlSchema);
  const { status: statusFilter, action, sort, view, propertyId, page } = urlState;
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Story 2-72c: `status`/`propertyId` both travel to the SERVER now
  // (`ListInspectionsQuery`) — the pre-migration comment here recorded why
  // `status` alone used to be impossible (the legacy Joi schema only
  // accepted three of the ten values above); that constraint is gone with
  // the Joi schema it described (deleted in the same story, not left
  // orphaned).
  // Named `queryFilters`, not `filters` — the module-scope `filters` array a
  // few lines up is the status PILL LIST the `<Select>` below maps over; a
  // same-named local here would shadow it.
  const queryFilters = useMemo(
    () => ({
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(propertyId ? { propertyId } : {}),
    }),
    [statusFilter, propertyId],
  );

  const {
    items: inspections,
    total,
    pageCount,
    hasMore: hasMoreFromServer,
    fetchNextPage,
    isLoading,
    isLoadingMore,
    isError,
    error,
    refetch,
  } = useCursorList<InspectionListItem>({
    queryKey: inspectionsKeys.list({ org: user?.organizationId, ...queryFilters }),
    fetchPage: (pageParams) => listInspections({ ...pageParams, ...queryFilters }),
    limit: PAGE_SIZE,
    enabled: Boolean(user),
  });

  // Story 2-72c (G-25): restore the URL-owned page count on a deep link or
  // reload by re-issuing `fetchNextPage()` until `pageCount` catches up —
  // the exact mechanism TemplateLibraryPage's identical effect already
  // established for its own cursor pages. The URL is the source of truth
  // driving the fetch, not the other way round: the "Load more" control
  // below only ever bumps the URL forward; this effect turns that into real
  // requests, for both a user click and a hydration catch-up after the
  // first page resolves.
  useEffect(() => {
    if (isLoading || isLoadingMore) return;
    if (pageCount === 0) return; // first page still in flight
    if (page + 1 > pageCount && hasMoreFromServer) {
      fetchNextPage();
    }
  }, [isLoading, isLoadingMore, pageCount, page, hasMoreFromServer, fetchNextPage]);

  // Shared cursor envelope (story 2-76). This is a PICKER source, not a list
  // page: it needs "are there any, and which can I choose" rather than a paged
  // surface, so it reads one page. The G-18 lint rule flags it at `warn` — Epic
  // 2Y / 2-72 decides whether it becomes a cursor list or a search-backed
  // picker.
  //
  // Story 3-3b: sourced from the CANONICAL /v1/subjects surface (3-1b) rather
  // than legacy /v1/properties, because a Subject carries `domainKey` and the
  // TypePicker filters on it (P03 §7.2). Ids are unchanged — 3-1a preserved
  // `subjectId === propertyId` — so the create payload's legacy `propertyId`
  // field keeps working through the compatibility window (P03 §6).
  const { data: sitesPage, isLoading: sitesLoading } = useQuery({
    queryKey: subjectsKeys.list({ org: user?.organizationId, picker: true }),
    queryFn: () => listSubjects({ status: 'active' }),
    enabled: Boolean(user),
  });
  const sites: Subject[] = sitesPage?.items ?? [];

  // Picker source, same G-18 warn as the sites query above — CreateInspectionDialog
  // only ever reads `id`/`name`/`isDefault` off a preset (never `schema`), so
  // the library LIST (story 4-2a) is a trivial swap: no field this dialog uses
  // is missing from `TemplateListItem`. `_id` (the dialog's field name, kept
  // from the legacy `ReportPreset` shape it still types against) is mapped
  // from the list item's `id` — the same Mongo id, just renamed at the
  // library-list boundary.
  const { data: templatesPage } = useQuery({
    queryKey: templatesKeys.list({ org: user?.organizationId, picker: true }),
    queryFn: () => listTemplates({ limit: 100 }),
    enabled: Boolean(user),
  });
  const presets = useMemo(
    () => (templatesPage?.items ?? []).map((t) => ({ _id: t.id, name: t.name, isDefault: t.isDefault })),
    [templatesPage],
  );
  const canCreate = sites.length > 0;

  useEffect(() => {
    if (action === 'new-inspection') {
      setShowDialog(true);
    }
  }, [action]);

  const closeDialog = () => {
    setShowDialog(false);
    if (action) {
      setUrlState({ action: '' });
    }
  };

  // Story 2-139: client-side re-sort over EVERY page loaded so far (not just
  // the latest) — see the `SortFilter` comment above for why this never
  // becomes a server request. `status`/`propertyId` are applied server-side
  // now (2-72c), so `inspections` here is already the filtered set.
  const sorted = useMemo(() => {
    if (sort !== 'property') return inspections;
    return [...inspections].sort((a, b) => propertyLabel(a).localeCompare(propertyLabel(b)));
  }, [inspections, sort]);

  return (
    <div className="space-y-6">
      <EntityListPage
        title="Inspection library"
        description="Search, filter, and manage all inspection records."
        actions={
          // Story 2-147wb: was a raw `flex flex-wrap items-center gap-2` div
          // mixing a `SegmentedControl` with a directly-variant/sized
          // `Button` — `SegmentedControl` is not an `ActionDescriptor` atom,
          // so it stays a `Cluster` sibling of `HeaderActions` (story
          // 2-147r) rather than being folded into that host, same "host
          // owns the Button/IconButton cluster, Cluster owns everything
          // else in the row" split `DashboardPage.tsx` already established.
          <Cluster gap={2}>
            {/* Story 2-139: the collection view-mode toggle (P10 §2.4). List
                (rows) is the default, matching the form rule's "operational
                work" reading of an inspection row; Cards is the touch-
                optimized alternate — a genuinely different rendering for a
                genuinely different task, not a dead second view. */}
            <SegmentedControl
              aria-label="View"
              value={view}
              onChange={(next) => setUrlState({ view: next })}
              options={[
                { value: 'list', label: 'List', icon: <ListBulletIcon className="h-4 w-4" aria-hidden="true" /> },
                { value: 'cards', label: 'Cards', icon: <Squares2X2Icon className="h-4 w-4" aria-hidden="true" /> },
              ]}
            />
            <HeaderActions
              actions={[
                {
                  id: 'new-inspection',
                  label: 'New inspection',
                  variant: 'primary',
                  // Story 2-147wb: header-action consistency pass — every
                  // primary "create a new record" header action across
                  // Sites/Inspections/Templates now carries the same
                  // leading `+`, matching `SitesPage`'s "Add {noun}" and
                  // `TemplateLibraryPage`'s "Create template".
                  icon: <PlusIcon className="h-4 w-4" aria-hidden="true" />,
                  onClick: () => {
                    setUrlState({ action: 'new-inspection' });
                    setShowDialog(true);
                  },
                  disabled: !canCreate,
                },
              ]}
            />
          </Cluster>
        }
        filters={
          <Cluster gap={2}>
            {/* Story 2-147k: was a row of raw `<button>` pills (G-22, DOD §4)
                with `aria-pressed` — the wrong ARIA shape for a mutually-
                exclusive single-select group besides (that role announces
                independently-toggleable buttons, not "choose exactly one").
                `Select` is the exact primitive/convention every sibling
                collection page's own status filter already uses (SitesPage,
                TemplateLibraryPage, ReviewQueuePage, ReportsListPage,
                InspectionTypesPage) — converged onto it here too, same
                `aria-label`/`data-testid` shape. No spec depended on the old
                per-value `filter-status-${value}` testid or `aria-pressed`
                (grepped fresh across e2e/specs/ — zero hits); the one real
                consumer, story-1G-13-urlstate.spec.mjs, queried this control
                by role="button"/name and is updated in the same story. */}
            <Select
              size="sm"
              value={statusFilter}
              aria-label="Status"
              data-testid="filter-status"
              onChange={(event) => setUrlState({ status: event.target.value as StatusFilter, page: 0 })}
            >
              {filters.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </Select>
            {/* Story 2-139: URL-owned sort (G-25) — client-side, see the
                `SortFilter` comment above. */}
            <Select
              size="sm"
              value={sort}
              aria-label="Sort"
              data-testid="filter-sort"
              onChange={(event) => setUrlState({ sort: event.target.value as SortFilter, page: 0 })}
            >
              {SORT_VALUES.map((value) => (
                <option key={value} value={value}>
                  {SORT_LABEL[value]}
                </option>
              ))}
            </Select>
          </Cluster>
        }
        isLoading={isLoading}
        empty={
          isError
            ? {
                // Same G-27/permission-revoked pattern TemplateLibraryPage
                // already established for its own list read.
                title:
                  errorStatus(error) === 403 || errorStatus(error) === 401
                    ? 'Your access to inspections changed'
                    : 'That list could not be loaded',
                description: errorMessage(error),
                actionLabel: 'Retry',
                onAction: refetch,
              }
            : {
                title: 'No inspections match this filter',
                description: 'Adjust filters or create a new inspection to get started.',
                actionLabel: 'Start new inspection',
                onAction: () => {
                  setUrlState({ action: 'new-inspection' });
                  setShowDialog(true);
                },
              }
        }
        footer={
          // Story 2-147tb: converged onto the canonical `LoadMorePager`
          // (P10 §2.7 row 7). Story 2-72c: `onLoadMore` bumps the URL-owned
          // page count rather than calling `fetchNextPage()` directly — the
          // restore effect above is what actually fetches, for this click
          // AND for a reload/deep link (same split TemplateLibraryPage's
          // identical footer already uses).
          <LoadMorePager
            shownCount={sorted.length}
            total={total}
            hasMore={hasMoreFromServer}
            onLoadMore={() => setUrlState({ page: page + 1 })}
            isLoadingMore={isLoadingMore}
          />
        }
      >
        {sorted.length === 0 ? false : view === 'cards' ? <ItemCards inspections={sorted} /> : <ItemList inspections={sorted} />}
      </EntityListPage>

      <CreateInspectionDialog
        isOpen={showDialog}
        onClose={closeDialog}
        sites={sites}
        presets={presets}
        isLoadingSites={sitesLoading}
        onRequestCreateSite={() => navigate('/app/sites')}
        onCreated={async (inspectionId) => {
          // Story 2-72c: every list surface reading inspections shares the
          // `inspectionsKeys.lists()` prefix now, same convergence
          // TemplateLibraryPage's identical invalidation already uses.
          await queryClient.invalidateQueries({ queryKey: inspectionsKeys.lists() });
          // The step-advance call into the retired `OnboardingTour` (`updateOnboarding
          // ({ step: 3, ... })`) is gone as of story 2-106 — the checklist that
          // replaces it derives "an inspection was created" from a real record on
          // every read, never a client-persisted step counter.
          closeDialog();
          navigate(`/app/items/${inspectionId}`);
        }}
      />
    </div>
  );
}
